import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import type {RootState} from "../app/store";
import {config} from "../config";
import {ResourceId} from "../types/resourceId";
import {CreateTaskDto, TaskDto, UpdateTaskDto} from "../types/task";
import {UpdateTemplate} from "../types/templates";

export const tasksListApi = createApi({
  reducerPath: "taskApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.apiBaseUrl,
    prepareHeaders: (headers, {getState}) => {
      const token = (getState() as RootState)?.auth?.accessToken;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["task"],
  endpoints: (builder) => ({
    getTasksList: builder.query<TaskDto[], void>({
      query: () => "/task",
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
          [
            ...result.map(
              ({id}) => ({type: 'task', id}) as const), {type: 'task', id: `LIST`},
          ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
          [{type: 'task', id: 'LIST'}],
    }),

    getTask: builder.query<UpdateTaskDto, string>({
      query: (id) => "/task/"+id,
    }),

    updateTask: builder.mutation<null,UpdateTaskDto>({
      query: (body) => ({
        url: "/task/"+body.id,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [{type: 'task', id: arg.id}],
    }),

    createTask: builder.mutation<ResourceId, CreateTaskDto >({
      query: (request) => {

        return {
          url: "/task",
          method: "POST",
          body: request,
          // Do NOT set Content-Type manually for multipart:
          // fetch will set the correct boundary for FormData.
        };
      },
      invalidatesTags: (result) =>
        result
          ? [{ type: "task", id: "LIST" }, { type: "task", id: result.id }]
          : [{ type: "task", id: "LIST" }],
    }),

    deleteTask: builder.mutation<null, string>({
      query: (id) => ({
        url: `/task/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{type: 'task', id}],
    }),


  }),
});

export const {
  useGetTasksListQuery,
  useCreateTaskMutation,
  useLazyGetTaskQuery,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} = tasksListApi;
